// Import custom IssueLabelToken until https://github.com/github/primer/issues/1142 is fixed
import {RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {Link, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import type {Label$key} from './__generated__/Label.graphql'
import styles from './Label.module.css'

type Props = {
  label: Label$key
  hidden?: boolean
  /**
   * Href getter for the label link
   * @param name Label name
   * @returns href
   */
  getLabelHref: (name: string) => string
  fontWeight?: number
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const labelQuery = graphql`
  fragment Label on Label {
    id
    nameHTML
    color
    name
    description
    archivedAt
  }
`

export function Label({label, hidden = false, getLabelHref, fontWeight}: Props) {
  const labelData = useFragment(labelQuery, label)
  const {id, name, description} = labelData

  return (
    <Tooltip
      text={description ?? ''}
      type="description"
      className={clsx(styles.LabelTooltip, !description && styles.tooltipHidden)}
    >
      {/*
        Remove link from layout and accessibility tree if hidden;
        display:none does not work because of label truncation calculations
      */}
      <Link
        href={getLabelHref(name)}
        className={clsx({'sr-only': hidden}, hidden && styles.linkHidden)}
        aria-describedby={`${id}-tooltip`}
      >
        <RepositoryLabelToken label={labelData} interactive key={id} fontWeight={fontWeight} />
        <span className="sr-only" id={`${id}-tooltip`}>
          {description ?? ''}
        </span>
      </Link>
    </Tooltip>
  )
}
